import { Box, debounce, Link } from "@mui/material";
import { styled } from "@mui/material/styles";
import { MdsExpandLess, MdsExpandMore } from "@sinch/icons";
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";

/**
 * A React functional component for rendering a row with expandable and collapsible functionality.
 * It monitors its content for overflow and provides UI controls to expand or collapse the content accordingly.
 *
 * @param children - The content to be rendered inside the row.
 */
export const ExtendableRow = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const [overflown, setOverflown] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const divElem = useRef<HTMLDivElement>(null);

  const checkOverflow = debounce(({ target: element }: ResizeObserverEntry) => {
    setOverflown(element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight);
  }, 100);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      checkOverflow(entries[0]);
    });
    if (divElem.current) {
      resizeObserver.observe(divElem.current);
    }
    return () => {
      if (divElem.current) {
        resizeObserver.unobserve(divElem.current);
      }
    };
  }, []);

  const expand = useCallback(() => setExpanded(true), []);
  const collapse = useCallback(() => setExpanded(false), []);

  return (
    <ExtandableRowContainer expanded={expanded}>
      <ContentBox ref={divElem} expanded={expanded}>
        {children}
      </ContentBox>
      {!expanded && overflown && (
        <ExpandLink onClick={expand}>
          <span>{t("more")}</span>
          <MdsIcon fontSize="xs" icon={MdsExpandMore} />
        </ExpandLink>
      )}
      {expanded && (
        <CollapseContainer>
          <CollapseLink onClick={collapse}>
            <span>{t("showLess")}</span>
            <MdsIcon fontSize="xs" icon={MdsExpandLess} />
          </CollapseLink>
        </CollapseContainer>
      )}
    </ExtandableRowContainer>
  );
};

const ExtandableRowContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded }) => ({
  display: "flex",
  flexDirection: expanded ? "column" : "row",
  width: "100%",
  maxWidth: "100%",
  flexWrap: "nowrap",
}));

const ContentBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded }) => ({
  whiteSpace: expanded ? "normal" : "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  width: "100%",
  maxWidth: "100%",
}));

const ExpandLink = styled(Link)(({ theme }) => ({
  paddingLeft: theme.spacing(0.5),
  display: "flex",
  alignItems: "center",
}));

const CollapseContainer = styled(Box)({
  textAlign: "right",
  width: "100%",
});

const CollapseLink = styled(Link)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  width: "100%",
});
